
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Event
 *
 * A significant date or occurrence attached to a contact, such as a legal case milestone (Original Petition, Date of Divorce, Court Hearing) or a biographical fact (Date of Birth, Date of Death, Marriage, Address Change). Events record when something happened, how long it lasted, where it occurred, and who was involved.
 */
class Event extends Base implements EntityType {
    eventId: uuid;
    category: uuid;
    description?: string | null;
    duration?: number | null;
    location?: string | null;
    occurredOn: Date;
    participants: uuid[];
    allDay: boolean;
    added: uuid[];

    /**
     * Create a new Event, with an (optional) JSON object or Event class
     *
     * @param {Object|Event} doc - Can be either a simple JSON object or a Event class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Event | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            eventId: genUUID(),
            category: null,
            occurredOn: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'event';
        // @ts-ignore
        this.eventId = doc._eventId || doc.eventId || doc.eventid || genUUID();
        // @ts-ignore
        this.category = doc._category || doc.category || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.duration = util.coalesce(util.parseFloat(doc._duration), util.parseFloat(doc.duration), null);
        // @ts-ignore
        this.location = doc._location || doc.location || null;
        // @ts-ignore
        this.occurredOn = DateUtil.timestampOnly(doc._occurredOn) || DateUtil.timestampOnly(doc.occurredOn) || DateUtil.timestampOnly(doc.occurredon) || null;
        // @ts-ignore
        this.participants = doc._participants || doc.participants || [];
        // @ts-ignore
        this.allDay = util.defaultBoolean([doc.allDay, doc._allDay, doc.allday, true]);
        // @ts-ignore
        this.added = doc._added || doc.added || [];

    }
    /**
     * @returns {uuid} This is a generic pointer to the eventId across all entity types
     */
    get id(): uuid {
        return this.eventId;
    }


    /**
     * Add a participant to event.participants
     *
     * @param {uuid} participant - The Participant to add
     * @returns uuid - The participant with any defaulted fields set
     */
    addParticipant(participant: uuid) : uuid {
        if (!participant) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.participants) this.participants = [];
        this.participants.push(participant);

        return participant;
    }

    /**
     * Update a participant from event
     *
     * @param {uuid} participant - The Participant to remove
     */
    updateParticipant(participant: uuid) {
        if (!this.participants) this.participants = [];
        
        // Find the matching object by participant.participantId
        const index = this.participants.findIndex(item => item === participant);
        if (index !== -1) {
            this.participants[index] = participant;
        }
        else this.addParticipant(participant);
    }

    /**
     * Remove a participant from event
     *
     * @param {uuid} participant - The Participant to remove
     */
    removeParticipant(participant: uuid) {
        if (!this.participants) return;
        
        // Find the matching object by participant.participantId
        let foundItems = this.participants?.filter(item => item === participant);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.participants = this.participants.filter(item => !(item === participant));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonParticipants() {
        this.participants = [];
     }
        
    /**
     * Add a added to event.added
     *
     * @param {uuid} added - The Added to add
     * @returns uuid - The added with any defaulted fields set
     */
    addAdded(added: uuid) : uuid {
        if (!added) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.added) this.added = [];
        this.added.push(added);

        return added;
    }

    /**
     * Update a added from event
     *
     * @param {uuid} added - The Added to remove
     */
    updateAdded(added: uuid) {
        if (!this.added) this.added = [];
        
        // Find the matching object by added.addedId
        const index = this.added.findIndex(item => item === added);
        if (index !== -1) {
            this.added[index] = added;
        }
        else this.addAdded(added);
    }

    /**
     * Remove a added from event
     *
     * @param {uuid} added - The Added to remove
     */
    removeAdded(added: uuid) {
        if (!this.added) return;
        
        // Find the matching object by added.addedId
        let foundItems = this.added?.filter(item => item === added);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.added = this.added.filter(item => !(item === added));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonAdded() {
        this.added = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for the event"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.eventId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["participants", "added"];
    }

    /**
     * Default entries are Outlaw-supplied Event entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Event[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'event',
            table: 'EVENT',
            comment: 'A significant date or occurrence attached to a contact, such as a legal case milestone (Original Petition, Date of Divorce, Court Hearing) or a biographical fact (Date of Birth, Date of Death, Marriage, Address Change). Events record when something happened, how long it lasted, where it occurred, and who was involved.',
            fields: {
                eventId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for the event'
                },
                category: {
                    type: 'uuid',
                    entityType: 'lookup',
                    lookupPaths: ['contact:event:category'],
                    required: true,
                    comment: 'Type of event, like a marriage or arrest'
                },
                description: {
                    type: 'string',
                    required: false,
                    comment: 'Short description of the event'
                },
                duration: {
                    type: 'float',
                    required: false,
                    comment: 'Duration of the event in hours; use the All Day field to mark an all-day event'
                },
                location: {
                    type: 'string',
                    required: false,
                    comment: 'Where the event will be located'
                },
                occurredOn: {
                    type: 'timestamp',
                    required: true,
                    comment: 'The date the event occurred'
                },
                participants: {
                    type: 'uuid[]',
                    entityType: 'participant',
                    comment: 'The people involved with the event'
                },
                allDay: {
                    type: 'boolean',
                    default: true,
                    comment: 'Is this an all-day event? If so, time will be 12am, otherwise we can about the time'
                },
                // Transient fields
                added: {
                    type: 'uuid[]',
                    transient: true,
                    comment: 'Added contact IDs'
                }
            },
            maps: [{
                    name: 'entityId',
                    keysFn: a => a.entityId,
                    presentFn: (a, k) => a.entityId === k && a.status !== 'D'
                },
                {
                    name: 'category',
                    keysFn: t => t.category,
                    presentFn: (t, k) => t.category === k
                },
                {
                    name: 'participants',
                    keysFn: t => t.participants,
                    presentFn: (t, k) => t.participants?.includes(k)
                }
            ]
        });
    }

}
export default Event;
